/**
 * Financial Types and Enums
 *
 * Core types for the financial data model.
 * These map to the Supabase PostgreSQL enums.
 *
 * Note: Prefixed with "Financial" to avoid conflicts with operational types.
 */

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Type of financial transaction
 */
export type FinancialTransactionType =
  | 'sale'           // Normal sale
  | 'refund'         // Full or partial refund
  | 'void'           // Cancelled before settlement
  | 'tip_adjust'     // Tip added/modified after auth
  | 'chargeback'     // Customer dispute
  | 'fee'            // Platform/processor fee
  | 'payout';        // Deposit to bank

/**
 * Payment method used in financial records
 */
export type FinancialPaymentMethod =
  | 'credit'
  | 'debit'
  | 'cash'
  | 'gift_card'
  | 'house_account'
  | 'third_party'    // Uber/DD handles payment
  | 'comp'           // Comped/free
  | 'check'          // Paper check tender
  | 'other';

/**
 * Source of the order for financial tracking
 */
export type FinancialOrderSource =
  | 'pos'            // In-house POS
  | 'online'         // Direct online ordering
  | 'kiosk'          // Self-service kiosk
  | 'phone'          // Phone order
  | 'uber_eats'
  | 'doordash'
  | 'grubhub'
  | 'other';

/**
 * Third-party payout status
 */
export type PayoutStatus =
  | 'pending'        // Expected but not received
  | 'processing'     // Platform processing
  | 'deposited'      // Money received
  | 'reconciled'     // Matched with platform report
  | 'disputed';      // Discrepancy found

/**
 * Third-party platforms
 */
export type ThirdPartyPlatform =
  | 'uber_eats'
  | 'doordash'
  | 'grubhub'
  | 'caviar'
  | 'postmates'
  | 'other';

/**
 * Financial reconciliation status
 */
export type FinancialReconciliationStatus =
  | 'matched'        // Internal matches external
  | 'discrepancy'    // Mismatch found
  | 'pending'        // Not yet reconciled
  | 'error';         // Failed to reconcile

// =============================================================================
// HELPER TYPES
// =============================================================================

/**
 * Money amount in cents (integer)
 * All financial amounts are stored as cents to avoid floating point issues
 */
export type CentsAmount = number;

/**
 * Convert dollars to cents
 */
export function toCents(dollars: number): CentsAmount {
  return Math.round(dollars * 100);
}

/**
 * Convert cents to dollars
 */
export function toDollars(cents: CentsAmount): number {
  return cents / 100;
}

/**
 * Format cents as currency string
 */
export function formatCents(cents: CentsAmount, locale = 'en-US', currency = 'USD'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}
